import { http } from '@/api/client/httpClient';
import type { PagedResult } from '@/api/types/abp';

// Finkit.DataSources.DataSourceConnectionAppService -> /api/app/data-source-connection
// Finkit.DataSources.DataSourceAppService             -> /api/app/data-source
//
// A tenant's Connectors are modelled in two levels, mirroring the legacy
// Connectors tab (TenantConnectors.cshtml):
//   Connection (Guid)  — the connector config (MT4/MT5 API, DB, terminal, ...).
//   DataSource (int)   — an account/feed that lives under a Connection.
const CONN_BASE = '/api/app/data-source-connection';
const DS_BASE = '/api/app/data-source';

// Finkit.Enums.DataSources.DataSourceTypeForSelect — int values are sent on
// create/update. Labels match the [Display(Name=...)] attributes and the
// display strings returned by the list endpoints.
export const CONNECTOR_TYPES: { value: number; label: string }[] = [
  { value: 0, label: 'MT4 Api' },
  { value: 1, label: 'MT5 Api' },
  { value: 2, label: 'MT4 Database' },
  { value: 3, label: 'MT5 Database' },
  { value: 4, label: 'MT5 PostgresSQL Database' },
  { value: 5, label: 'MT4 PostgresSQL Database' },
  { value: 6, label: 'Client Database' },
  { value: 7, label: 'External Database' },
  { value: 8, label: 'MT4 Terminal' },
  { value: 9, label: 'MT5 Terminal' },
  { value: 11, label: 'Match Trader' },
  { value: 12, label: 'Match Trader API' },
];

export function connectorTypeValueFromLabel(label: string | null | undefined): number | null {
  const hit = CONNECTOR_TYPES.find((t) => t.label === label);
  return hit ? hit.value : null;
}

// Terminal-style connectors (MT4/MT5 Terminal, Match Trader, Match Trader API)
// own multiple child DataSources, so only they get the "Edit Data Sources"
// action. Mirrors isTerminalType() in ConnectorsModal/js/connector.js.
export function isTerminalConnectorType(label: string | null | undefined): boolean {
  const s = (label ?? '').toString().trim().toLowerCase();
  return s === 'mt4 terminal' || s === 'mt5 terminal' || s.startsWith('match trader');
}

// Which connection fields are relevant for a given type. Mirrors the field
// show/hide logic in ConnectorsModal/js/create.js (classifyByText + applyVisibility).
export interface ConnectorFieldVisibility {
  url: boolean;
  login: boolean;
  password: boolean;
  port: boolean;
  database: boolean;
  schema: boolean;
  manager: boolean;
}

export function connectorFieldVisibility(typeValue: number | null): ConnectorFieldVisibility {
  const label = (CONNECTOR_TYPES.find((t) => t.value === typeValue)?.label ?? '').toLowerCase();
  const isMatchTrader = label.includes('match trader');
  const isApi = !isMatchTrader && label.includes('api');
  const isPg = label.includes('postgres');
  const isClientOrExternal = label.includes('client database') || label.includes('external database');
  const isMt5Db = label.includes('mt5 database');
  const isMt4 = /\bmt4\b/.test(label);
  const isMt4Pg = isMt4 && isPg;

  const showDbSchema = !(isApi || isClientOrExternal);
  const showManager = (isPg && !isMt4Pg) || isMt5Db;

  return {
    url: true,
    login: true,
    password: true,
    port: true,
    database: showDbSchema,
    schema: showDbSchema,
    manager: showManager,
  };
}

// ---------------------------------------------------------------------------
// Connections
// ---------------------------------------------------------------------------

export interface ConnectionRow {
  id: string;
  name: string;
  dataSourceType: string; // display label, e.g. "MT4 Api"
  tenantId: string;
  enabled: boolean;
}

export interface ConnectionDetail {
  id: string;
  tenantId: string;
  name: string;
  url: string | null;
  login: string | null;
  database: string | null;
  schema: string | null;
  password: string | null;
  port: string | null;
  managerLogin: string | null;
  dataSourceType: number;
  enabled: boolean;
}

export interface ConnectionCreateInput {
  tenantId: string;
  name: string;
  dataSourceType: number;
  url?: string | null;
  login?: string | null;
  database?: string | null;
  schema?: string | null;
  password?: string | null;
  port?: string | null;
  managerLogin?: string | null;
}

export type ConnectionUpdateInput = ConnectionCreateInput & { id: string; enabled: boolean };

export const connectionsApi = {
  async getList(
    tenantId: string,
    input: { name?: string; skipCount: number; maxResultCount: number },
  ): Promise<PagedResult<ConnectionRow>> {
    const { data } = await http.get<PagedResult<ConnectionRow>>(`${CONN_BASE}/for-data-table`, {
      params: { tenantId, ...input },
    });
    return data;
  },
  async get(id: string, tenantId: string): Promise<ConnectionDetail> {
    const { data } = await http.get<ConnectionDetail>(`${CONN_BASE}/${id}`, { params: { tenantId } });
    return data;
  },
  async create(input: ConnectionCreateInput): Promise<void> {
    const { tenantId, ...body } = input;
    await http.post(CONN_BASE, { ...body, tenantId }, { params: { tenantid: tenantId } });
  },
  async update(input: ConnectionUpdateInput): Promise<void> {
    const { tenantId, ...body } = input;
    await http.put(CONN_BASE, { ...body, tenantId }, { params: { tenantid: tenantId } });
  },
  async remove(id: string, tenantId: string): Promise<void> {
    await http.delete(`${CONN_BASE}/${id}`, { params: { tenantId } });
  },
  async setEnabled(id: string, enabled: boolean, tenantId: string): Promise<void> {
    await http.post(`${CONN_BASE}/${id}/set-enabled`, { enabled }, { params: { Tenantid: tenantId } });
  },
};

// ---------------------------------------------------------------------------
// Data sources (children of a connection)
// ---------------------------------------------------------------------------

export interface DataSourceRow {
  id: number;
  tenantId: string;
  name: string;
  server: string | null;
  accountLogin: string | null;
  password: string | null;
  enabled: boolean;
}

export interface DataSourceCreateInput {
  tenantId: string;
  dataSourceConectionId: string;
  name: string;
  server?: string | null;
  accountLogin?: string | null;
  password?: string | null;
}

export type DataSourceUpdateInput = {
  tenantId: string;
  id: number;
  dataSourceConnectionId: string;
  name: string;
  server?: string | null;
  accountLogin?: string | null;
  password?: string | null;
};

export interface DataSourceDetail {
  id: number;
  tenantId: string;
  name: string;
  server: string | null;
  accountLogin: string | null;
  password: string | null;
  dataSourceConnectionId: string;
}

export const dataSourcesApi = {
  async get(id: number, tenantId: string): Promise<DataSourceDetail> {
    const { data } = await http.get<DataSourceDetail>(`${DS_BASE}/${id}`, { params: { tenantId } });
    return data;
  },
  async getList(
    tenantId: string,
    connectionId: string,
    input: { name?: string; skipCount: number; maxResultCount: number },
  ): Promise<PagedResult<DataSourceRow>> {
    const { data } = await http.get<PagedResult<DataSourceRow>>(`${DS_BASE}/for-data-table`, {
      params: { tenantId, DataSourceConnectionId: connectionId, ...input },
    });
    return data;
  },
  async create(input: DataSourceCreateInput): Promise<void> {
    const { tenantId, ...body } = input;
    await http.post(DS_BASE, { ...body, tenantId }, { params: { tenantId } });
  },
  async update(input: DataSourceUpdateInput): Promise<void> {
    const { tenantId, ...body } = input;
    await http.put(DS_BASE, { ...body, tenantId }, { params: { tenantId } });
  },
  async remove(id: number, tenantId: string): Promise<void> {
    await http.delete(`${DS_BASE}/${id}`, { params: { tenantId } });
  },
  async setEnabled(id: number, enabled: boolean, tenantId: string): Promise<void> {
    await http.post(`${DS_BASE}/${id}/set-enabled/${tenantId}`, { enabled });
  },
};
